import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FilterableMultiSelect,
  Layer,
  DatePicker,
  DatePickerInput,
} from '@carbon/react';
import {
  DropdownField,
  NumberField,
  numberToPlainString,
} from 'components/form-fields-2';
import { debounce } from 'lodash-es';
import { useAtomValue } from 'jotai';
import { api } from 'lib/request/api';
import { Field } from 'components/field';
import { format0 } from 'utils/number';
import { parseMomentUtc } from 'utils/dates';

import type { TypeCurveWorkspaceResponse } from 'types/type-curve-workspace';
import { tcWorkspaceTasksAtom } from 'components/type-curve-workspace/tasks';

import type { TypeCurveDraft } from './types';
import { FieldVerifyIcon } from './field-verify-icon';
import style from './type-curve-details-section.module.scss';

const DEBOUNCE_MS = 800;

// Draft stores numeric fields as strings; parse to a number for NumberField
// (null for blank / non-numeric in-progress input).
const toNum = (raw: string): number | null => {
  if (!raw.trim()) return null;
  const n = Number(raw.replace(/,/g, ''));
  return Number.isNaN(n) ? null : n;
};

// NumberField emits number | null; store back as a plain string in the draft.
const fromNum = (n: number | null): string => {
  return n === null ? '' : numberToPlainString(n);
};

// Text that reaches the vintage input without going through the calendar
// (browser autofill, typing) never fires flatpickr's onChange — its blur handler
// calls setDate(..., triggerChange: false) — so parse the raw `m/d/Y` ourselves.
const rawDateToIso = (raw: string): string | null => {
  const parts = raw.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!parts) return null;
  const [, m, d, y] = parts;
  const month = Number(m);
  const day = Number(d);
  const year = Number(y);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
    2,
    '0'
  )}`;
};

interface TypeCurveDetailsFormProps {
  workspace: TypeCurveWorkspaceResponse;
  draft: TypeCurveDraft;
  onDraftChange: (next: TypeCurveDraft) => void;
  typeCurveAreaName?: string | null;
  basin?: string | null;
  wellCount?: number | null;
  promoted: boolean;
  statusOptions?: string[];
  developmentTypeOptions?: string[];
  formationOptions?: string[];
  operatorOptions?: string[];
  basinOptions?: string[];
}

export const TypeCurveDetailsForm = ({
  workspace,
  draft,
  onDraftChange,
  typeCurveAreaName,
  basin,
  wellCount,
  promoted,
  statusOptions = [],
  developmentTypeOptions = [],
  formationOptions = [],
  operatorOptions = [],
  basinOptions = [],
}: TypeCurveDetailsFormProps) => {
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // Debounced save to backend
  const debouncedSave = useMemo(
    () =>
      debounce((next: TypeCurveDraft) => {
        void api
          .patch(`type_curve_workspaces/${workspace.id}/header`, {
            json: {
              header: {
                status: next.status || null,
                lateral_length_ft: next.lateralLength
                  ? Number(next.lateralLength.replace(/,/g, ''))
                  : null,
                formation: next.formation || null,
                well_spacing_ft: next.wellSpacing
                  ? Number(next.wellSpacing.replace(/,/g, ''))
                  : null,
                proppant_intensity_lbs_ft: next.proppantIntensity
                  ? Number(next.proppantIntensity.replace(/,/g, ''))
                  : null,
                fluid_intensity_gal_ft: next.fluidIntensityGal
                  ? Number(next.fluidIntensityGal.replace(/,/g, ''))
                  : null,
                fluid_intensity_bbl_ft: next.fluidIntensityBbl
                  ? Number(next.fluidIntensityBbl.replace(/,/g, ''))
                  : null,
                vintage_date: next.vintage || null,
                operator_names:
                  next.operators.length > 0 ? next.operators : null,
                development_type:
                  next.developmentType.length === 1
                    ? next.developmentType[0]
                    : null,
              },
            },
          })
          .json();
      }, DEBOUNCE_MS),
    [workspace.id]
  );

  // Cleanup debounced save on unmount
  useEffect(() => {
    return () => debouncedSave.cancel();
  }, [debouncedSave]);

  const update = useCallback(
    (
      field: keyof TypeCurveDraft,
      value: TypeCurveDraft[keyof TypeCurveDraft]
    ) => {
      const next = { ...draftRef.current, [field]: value };

      // Link gal/ft <-> bbl/ft
      if (field === 'fluidIntensityGal' && typeof value === 'string') {
        const gal = Number(value.replace(/,/g, ''));
        if (!isNaN(gal) && gal > 0) {
          next.fluidIntensityBbl = String(Math.round((gal / 42) * 100) / 100);
        }
      }
      if (field === 'fluidIntensityBbl' && typeof value === 'string') {
        const bbl = Number(value.replace(/,/g, ''));
        if (!isNaN(bbl) && bbl > 0) {
          next.fluidIntensityGal = String(Math.round(bbl * 42 * 100) / 100);
        }
      }

      onDraftChange(next);
      if (!promoted) {
        debouncedSave(next);
      }
    },
    [onDraftChange, debouncedSave, promoted]
  );

  // Per-field verification status keyed by backend task `path`. A field is
  // locked once its task is verified correct (edit again by undoing the verdict
  // in the tasks panel).
  const { tasks: workspaceTasks } = useAtomValue(tcWorkspaceTasksAtom);
  const statusByPath = useMemo(() => {
    const map: Record<string, 'correct' | 'incorrect' | null> = {};
    for (const t of workspaceTasks) map[t.path] = t.status;
    return map;
  }, [workspaceTasks]);
  const fieldDisabled = (path: string) =>
    promoted || statusByPath[path] === 'correct';

  // Label + verification icon rendered inside each input's own label slot
  // (labelText / titleText / label) so Carbon owns the single label element —
  // avoids stacking a separate <span> on top of the component's empty label,
  // which is what caused uneven field spacing.
  const renderLabel = (text: string, path: string) => (
    <span className={style.labelWithIcon}>
      {text} <FieldVerifyIcon path={path} />
    </span>
  );

  // Dropdown items (single-select) come from the area-details endpoint, unioned
  // with the current saved value so a value outside the option set still shows.
  const statusItems = useMemo(() => {
    const set = new Set(statusOptions);
    if (draft.status) set.add(draft.status);
    return Array.from(set);
  }, [statusOptions, draft.status]);

  const devType = draft.developmentType[0] ?? '';
  const developmentTypeItems = useMemo(() => {
    const set = new Set(developmentTypeOptions);
    if (devType) set.add(devType);
    return Array.from(set);
  }, [developmentTypeOptions, devType]);

  const formationItems = useMemo(() => {
    const set = new Set(formationOptions);
    if (draft.formation) set.add(draft.formation);
    return Array.from(set);
  }, [formationOptions, draft.formation]);

  // Operator multiselect items (plain string[]), unioned with current
  // selections so already-chosen operators remain selectable.
  const operatorItems = useMemo(
    () => Array.from(new Set([...operatorOptions, ...draft.operators])),
    [operatorOptions, draft.operators]
  );

  // Basin is an editable dropdown only when the area maps to multiple basins;
  // the selection is client-side only (not persisted via the header PATCH).
  // Default to the persisted basin, else the first option when none is set.
  const basinEditable = basinOptions.length > 1;
  const [selectedBasin, setSelectedBasin] = useState<string | null>(null);
  useEffect(() => {
    setSelectedBasin(basin || basinOptions[0] || null);
  }, [basin, basinOptions]);

  // Default Development Type to the first option when nothing is selected yet
  // (persists via the header PATCH like any other change).
  useEffect(() => {
    if (
      developmentTypeOptions.length > 0 &&
      draft.developmentType.length === 0
    ) {
      update('developmentType', [developmentTypeOptions[0]]);
    }
  }, [developmentTypeOptions, draft.developmentType, update]);

  return (
    <div className={style.detailsSection}>
      <div className={style.formGrid}>
        {/* Row 1 */}
        {/* Basin — editable dropdown when the area maps to multiple basins
            (client-side only), otherwise a read-only string. */}
        <div className={style.formField}>
          {basinEditable ? (
            <DropdownField
              id="tc-draft-basin"
              label="Select"
              titleText="Basin"
              items={basinOptions}
              itemToString={(item) => item ?? ''}
              value={selectedBasin || null}
              onChange={(v) => setSelectedBasin(v ?? null)}
              size="sm"
              autoAlign
            />
          ) : (
            <Field label="Basin" value={basin || '--'} />
          )}
        </div>

        {/* Well Count */}
        <div className={style.formField}>
          <Field
            label="Well Count"
            value={
              wellCount !== null && wellCount !== undefined
                ? format0(wellCount)
                : '--'
            }
          />
        </div>

        {/* Vintage */}
        <div className={style.formField}>
          <Layer>
            <DatePicker
              datePickerType="single"
              dateFormat="m/d/Y"
              // Parse the stored value in UTC (parseMomentUtc) and hand
              // flatpickr a string already formatted as `m/d/Y`, so it parses
              // via dateFormat with no local-timezone off-by-one. A raw ISO
              // string ("2026-07-20") can't be parsed as "m/d/Y" and shows blank.
              value={
                draft.vintage
                  ? parseMomentUtc(draft.vintage).format('MM/DD/YYYY')
                  : undefined
              }
              onChange={([date]) => {
                if (date) {
                  // Store ISO (YYYY-MM-DD) from LOCAL date parts (not
                  // toISOString, which shifts to UTC and can drop a day).
                  const iso = `${date.getFullYear()}-${String(
                    date.getMonth() + 1
                  ).padStart(2, '0')}-${String(date.getDate()).padStart(
                    2,
                    '0'
                  )}`;
                  update('vintage', iso);
                }
              }}
            >
              <DatePickerInput
                id="tc-draft-vintage"
                labelText={renderLabel('Vintage', 'vintage_date')}
                placeholder="mm/dd/yyyy"
                disabled={fieldDisabled('vintage_date')}
                size="sm"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const iso = rawDateToIso(e.target.value);
                  if (iso && iso !== draft.vintage) update('vintage', iso);
                }}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  const raw = e.target.value.trim();
                  if (!raw) {
                    if (draft.vintage) update('vintage', '');
                    return;
                  }
                  const iso = rawDateToIso(raw);
                  if (iso && iso !== draft.vintage) update('vintage', iso);
                }}
              />
            </DatePicker>
          </Layer>
        </div>

        {/* Status */}
        <div className={style.formField}>
          <DropdownField
            id="tc-draft-status"
            label="Select"
            titleText={renderLabel('Status', 'type_curve_status')}
            items={statusItems}
            itemToString={(item) => item ?? ''}
            value={draft.status || null}
            onChange={(v) => update('status', v ?? '')}
            disabled={fieldDisabled('type_curve_status')}
            size="sm"
            autoAlign
          />
        </div>

        {/* Development Type */}
        <div className={style.formField}>
          <DropdownField
            id="tc-draft-devtype"
            label="Select"
            titleText={renderLabel('Development Type', 'development_type')}
            items={developmentTypeItems}
            itemToString={(item) => item ?? ''}
            value={draft.developmentType[0] ?? null}
            onChange={(v) => update('developmentType', v ? [v] : [])}
            disabled={fieldDisabled('development_type') || !basinEditable}
            size="sm"
            autoAlign
          />
        </div>

        {/* Lateral Length (ft) */}
        <div className={style.formField}>
          <NumberField
            id="tc-draft-lateral-length"
            label={renderLabel('Lateral Length (ft)', 'lateral_length_ft')}
            value={toNum(draft.lateralLength)}
            onChange={(n) => update('lateralLength', fromNum(n))}
            disabled={fieldDisabled('lateral_length_ft')}
            size="sm"
          />
        </div>

        {/* Row 2 */}
        {/* Well Spacing (ft) */}
        <div className={style.formField}>
          <NumberField
            id="tc-draft-well-spacing"
            label={renderLabel('Well Spacing (ft)', 'well_spacing_ft')}
            value={toNum(draft.wellSpacing)}
            onChange={(n) => update('wellSpacing', fromNum(n))}
            disabled={fieldDisabled('well_spacing_ft')}
            size="sm"
          />
        </div>

        {/* Type Curve Area */}
        <div className={style.formField}>
          <Field label="Type Curve Area" value={typeCurveAreaName || '--'} />
        </div>

        {/* Type Curve Formation */}
        <div className={style.formField}>
          <DropdownField
            id="tc-draft-formation"
            label="Select"
            titleText={renderLabel('Type Curve Formation', 'formation')}
            items={formationItems}
            itemToString={(item) => item ?? ''}
            value={draft.formation || null}
            onChange={(v) => update('formation', v ?? '')}
            disabled={fieldDisabled('formation')}
            size="sm"
            autoAlign
          />
        </div>

        {/* Operator */}
        <div className={style.formField}>
          <Layer>
            <FilterableMultiSelect
              id="tc-draft-operators"
              titleText={renderLabel('Operator', 'operator_names')}
              placeholder="Select operators..."
              items={operatorItems}
              itemToString={(item) => item ?? ''}
              initialSelectedItems={draft.operators}
              onChange={({ selectedItems }) =>
                update('operators', selectedItems)
              }
              disabled={fieldDisabled('operator_names')}
              size="sm"
              autoAlign
            />
          </Layer>
        </div>

        {/* Proppant Intensity (lbs/ft) */}
        <div className={style.formField}>
          <NumberField
            id="tc-draft-proppant"
            label={renderLabel(
              'Proppant Intensity (lbs/ft)',
              'proppant_intensity_lbs_ft'
            )}
            value={toNum(draft.proppantIntensity)}
            onChange={(n) => update('proppantIntensity', fromNum(n))}
            disabled={fieldDisabled('proppant_intensity_lbs_ft')}
            size="sm"
          />
        </div>

        {/* Fluid Intensity (gal/ft) */}
        <div className={style.formField}>
          <NumberField
            id="tc-draft-fluid-gal"
            label={renderLabel(
              'Fluid Intensity (gal/ft)',
              'fluid_intensity_bbl_ft'
            )}
            value={toNum(draft.fluidIntensityGal)}
            onChange={(n) => update('fluidIntensityGal', fromNum(n))}
            disabled={fieldDisabled('fluid_intensity_bbl_ft')}
            size="sm"
          />
        </div>

        {/* Row 3 */}
        {/* Fluid Intensity (bbl/ft) */}
        <div className={style.formField}>
          <NumberField
            id="tc-draft-fluid-bbl"
            label={renderLabel(
              'Fluid Intensity (bbl/ft)',
              'fluid_intensity_bbl_ft'
            )}
            value={toNum(draft.fluidIntensityBbl)}
            onChange={(n) => update('fluidIntensityBbl', fromNum(n))}
            disabled={fieldDisabled('fluid_intensity_bbl_ft')}
            size="sm"
          />
        </div>
      </div>
    </div>
  );
};
